import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  XCircle,
} from 'lucide-react';
import { useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  CitationVerificationStatus,
  DemandReviewCitation,
  getVerificationStatusLabel,
} from '@/services/types/demand-review-types';

interface CitationViewerProps {
  citation: DemandReviewCitation;
  demandMarkdown: string | null;
}

function getStatusIcon(status: CitationVerificationStatus) {
  switch (status) {
    case CitationVerificationStatus.VERIFIED_SUPPORTED:
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case CitationVerificationStatus.VERIFIED_NOT_SUPPORTED:
      return <XCircle className="h-5 w-5 text-red-600" />;
    case CitationVerificationStatus.VERIFIED_PARTIAL:
      return <AlertCircle className="h-5 w-5 text-yellow-600" />;
    default:
      return <Clock className="h-5 w-5 text-gray-400" />;
  }
}

function getStatusColor(status: CitationVerificationStatus) {
  switch (status) {
    case CitationVerificationStatus.VERIFIED_SUPPORTED:
      return 'bg-green-50 border-green-200';
    case CitationVerificationStatus.VERIFIED_NOT_SUPPORTED:
      return 'bg-red-50 border-red-200';
    case CitationVerificationStatus.VERIFIED_PARTIAL:
      return 'bg-yellow-50 border-yellow-200';
    default:
      return 'bg-gray-50 border-gray-200';
  }
}

export const CitationViewer = ({
  citation,
  demandMarkdown,
}: CitationViewerProps) => {
  // Highlight the citation in the demand markdown
  const highlightedContext = useMemo(() => {
    if (!demandMarkdown || !citation.position_start || !citation.position_end) {
      return citation.context_text;
    }

    // Get a larger context around the citation
    const contextStart = Math.max(0, citation.position_start - 300);
    const contextEnd = Math.min(
      demandMarkdown.length,
      citation.position_end + 300,
    );
    const context = demandMarkdown.slice(contextStart, contextEnd);

    // Calculate the relative position of the citation in the extracted context
    const relativeStart = citation.position_start - contextStart;
    const relativeEnd = citation.position_end - contextStart;

    // Wrap the citation in a highlight span
    const before = context.slice(0, relativeStart);
    const highlighted = context.slice(relativeStart, relativeEnd);
    const after = context.slice(relativeEnd);

    return { before, highlighted, after };
  }, [demandMarkdown, citation]);

  const hasVerificationResult = citation.verification_result !== null;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon(citation.verification_status)}
            <CardTitle className="font-mono">{citation.bates_number}</CardTitle>
          </div>
          <Badge
            variant={
              citation.verification_status ===
              CitationVerificationStatus.VERIFIED_SUPPORTED
                ? 'default'
                : citation.verification_status ===
                    CitationVerificationStatus.VERIFIED_NOT_SUPPORTED
                  ? 'destructive'
                  : 'secondary'
            }
          >
            {getVerificationStatusLabel(citation.verification_status)}
          </Badge>
        </div>
        {citation.page_start && (
          <div className="text-sm text-muted-foreground">
            Page{citation.page_end ? 's' : ''} {citation.page_start}
            {citation.page_end && ` - ${citation.page_end}`}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[calc(100vh-500px)]">
          {/* Citation Context from Demand */}
          <div className="space-y-4">
            <div>
              <h3 className="mb-2 text-sm font-semibold">
                Citation Context in Demand
              </h3>
              <div
                className={`rounded-lg border p-4 ${
                  typeof highlightedContext === 'object'
                    ? 'bg-muted/30'
                    : 'bg-muted/30'
                }`}
              >
                {typeof highlightedContext === 'object' ? (
                  <p className="text-sm leading-relaxed">
                    <span className="text-muted-foreground">
                      {highlightedContext.before}
                    </span>
                    <mark className="rounded bg-yellow-200 px-1 font-semibold">
                      {highlightedContext.highlighted}
                    </mark>
                    <span className="text-muted-foreground">
                      {highlightedContext.after}
                    </span>
                  </p>
                ) : (
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {highlightedContext}
                  </p>
                )}
              </div>
            </div>

            <Separator />

            {/* Verification Result */}
            {hasVerificationResult && citation.verification_result && (
              <div>
                <h3 className="mb-2 text-sm font-semibold">
                  AI Verification Result
                </h3>
                <div
                  className={`rounded-lg border p-4 ${getStatusColor(citation.verification_status)}`}
                >
                  <div className="mb-2 flex items-center gap-2">
                    {getStatusIcon(citation.verification_status)}
                    <span className="font-medium capitalize">
                      {citation.verification_result.result.replace(/_/g, ' ')}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      (
                      {Math.round(
                        citation.verification_result.confidence * 100,
                      )}
                      % confidence)
                    </span>
                  </div>
                  <p className="text-sm">
                    {citation.verification_result.explanation}
                  </p>
                  {citation.verification_result.supporting_text && (
                    <div className="mt-3 rounded bg-white/50 p-3">
                      <p className="text-xs font-semibold text-muted-foreground">
                        Supporting Text from Exhibit:
                      </p>
                      <p className="mt-1 text-sm italic">
                        &ldquo;{citation.verification_result.supporting_text}
                        &rdquo;
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Exhibit Reference */}
            {citation.exhibit_id ? (
              <div>
                <h3 className="mb-2 text-sm font-semibold">Matched Exhibit</h3>
                <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-4">
                  <FileText className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      Page {citation.exhibit_page_number}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Exhibit ID: {citation.exhibit_id}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <p className="text-sm text-yellow-800">
                  No exhibit has been matched to this citation yet. Upload
                  exhibits and run the matching process.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
